import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  MenuItem,
  Snackbar,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  InputAdornment,
  Menu,
} from '@mui/material';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import apiAxiosInstance from '../../utils/axiosInstance';
import SectionEmptyState from '../Common/SectionEmptyState';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';

type CasePanelProps = {
  scope: 'student' | 'group' | null;
  id: string | null;
  name?: string;
  onLoadContext: (ctx: { case?: any; notes?: any[]; strategies?: any[] }) => void;
  onCaseCreated?: () => void;
  dataRefreshKey?: number;
};

const CasePanel: React.FC<CasePanelProps> = ({
  scope,
  id,
  name,
  onLoadContext,
  onCaseCreated,
  dataRefreshKey = 0,
}) => {
  const [cases, setCases] = useState<any[]>([]);
  const [selectedCase, setSelectedCase] = useState<any | null>(null);
  const [notes, setNotes] = useState<any[]>([]);
  const [strategies, setStrategies] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // New case dialog
  const [newCaseOpen, setNewCaseOpen] = useState(false);
  const [newCaseTitle, setNewCaseTitle] = useState('');
  const [newCaseTier, setNewCaseTier] = useState(1);

  // Edit title state
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [editTitleValue, setEditTitleValue] = useState('');

  // Three-dot menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuCaseId, setMenuCaseId] = useState<string | null>(null);

  // Toast
  const [toast, setToast] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error';
  } | null>(null);

  // Load all cases for this student/group
  const loadCases = useCallback(async () => {
    if (!scope || !id) {
      setCases([]);
      setSelectedCase(null);
      setNotes([]);
      setStrategies([]);
      return;
    }

    setLoading(true);
    try {
      const params =
        scope === 'student'
          ? { scope: 'student', studentId: id }
          : { scope: 'group', groupId: id };
      const res = await apiAxiosInstance.get('/api/behavior/cases', { params });
      const caseList = res.data || [];
      setCases(caseList);

      // Select the first active case by default, or the first case if none active
      const activeCase =
        caseList.find((c: any) => c.status === 'active') || caseList[0];
      if (activeCase) {
        setSelectedCase(activeCase);
        await loadCaseDetails(activeCase._id);
      } else {
        setSelectedCase(null);
        setNotes([]);
        setStrategies([]);
        onLoadContext({});
      }
    } catch (e) {
      console.error('Failed to load cases', e);
    } finally {
      setLoading(false);
    }
  }, [scope, id, onLoadContext]);

  // Load details for a specific case
  const loadCaseDetails = async (caseId: string) => {
    try {
      const [n, s] = await Promise.all([
        apiAxiosInstance.get(`/api/behavior/cases/${caseId}/notes`),
        apiAxiosInstance.get(`/api/behavior/cases/${caseId}/strategies`),
      ]);
      setNotes(n.data || []);
      setStrategies(s.data || []);
    } catch (e) {
      console.error('Failed to load case details', e);
    }
  };

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  useEffect(() => {
    if (selectedCase?._id && dataRefreshKey > 0) {
      loadCaseDetails(selectedCase._id);
    }
  }, [dataRefreshKey, selectedCase?._id]);

  // Update context when selected case or its data changes
  useEffect(() => {
    if (selectedCase) {
      onLoadContext({ case: selectedCase, notes, strategies });
    }
  }, [selectedCase, notes, strategies, onLoadContext]);

  // Handle creating a new case
  const handleCreateCase = async () => {
    if (!scope || !id) return;

    try {
      const payload = {
        scope: { type: scope, [`${scope}Id`]: id },
        status: 'active',
        tier: newCaseTier,
        title: newCaseTitle.trim() || undefined,
        targetBehaviors: [],
      };
      const res = await apiAxiosInstance.post('/api/behavior/cases', payload);
      const created = res.data;

      setCases((prev) => [created, ...prev]);
      setSelectedCase(created);
      await loadCaseDetails(created._id);

      setToast({ open: true, msg: 'New case created', sev: 'success' });
      setNewCaseOpen(false);
      setNewCaseTitle('');
      setNewCaseTier(1);

      if (onCaseCreated) {
        onCaseCreated();
      }
    } catch (e) {
      setToast({ open: true, msg: 'Failed to create case', sev: 'error' });
    }
  };

  // Handle updating case title
  const handleUpdateTitle = async (caseId: string) => {
    if (!editTitleValue.trim()) {
      setEditingTitle(null);
      return;
    }

    try {
      await apiAxiosInstance.patch(`/api/behavior/cases/${caseId}`, {
        title: editTitleValue.trim(),
      });

      setCases((prev) =>
        prev.map((c) =>
          c._id === caseId ? { ...c, title: editTitleValue.trim() } : c
        )
      );
      if (selectedCase?._id === caseId) {
        setSelectedCase({ ...selectedCase, title: editTitleValue.trim() });
      }

      setToast({ open: true, msg: 'Title updated', sev: 'success' });
      setEditingTitle(null);
    } catch (e) {
      setToast({ open: true, msg: 'Failed to update title', sev: 'error' });
    }
  };

  const startEditingTitle = (c: any) => {
    setEditingTitle(c._id);
    setEditTitleValue(c.title || '');
  };

  const cancelEditingTitle = () => {
    setEditingTitle(null);
    setEditTitleValue('');
  };

  // Three-dot menu handlers
  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, caseId: string) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuCaseId(caseId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuCaseId(null);
  };

  const handleDeleteCase = async () => {
    if (!menuCaseId) return;
    
    try {
      await apiAxiosInstance.delete(`/api/behavior/cases/${menuCaseId}`);
      
      setCases((prev) => prev.filter((c) => c._id !== menuCaseId));
      if (selectedCase?._id === menuCaseId) {
        setSelectedCase(null);
        setNotes([]);
        onLoadContext({});
      }
      
      setToast({ open: true, msg: 'Case deleted', sev: 'success' });
    } catch (e) {
      setToast({ open: true, msg: 'Failed to delete case', sev: 'error' });
    } finally {
      handleMenuClose();
    }
  };

  const getCaseDisplayTitle = (c: any) => {
    if (c.title) return c.title;
    return `Case (${c.status})`;
  };

  if (!scope || !id) {
    return (
      <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <SectionEmptyState
          icon={<FolderOpenOutlinedIcon sx={{ fontSize: 40 }} />}
          title="Cases"
          description="Select a student or group from the roster to create cases, log incidents, and review notes."
        />
      </Paper>
    );
  }

  return (
    <>
      <Paper
        sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant="h6">
            {name ? `${name}'s Cases` : 'Cases'}
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setNewCaseOpen(true)}
          >
            New Case
          </Button>
        </Box>

        {/* Case List */}
        <Typography variant="caption" color="text.secondary" gutterBottom>
          Select a case to view conversations and notes
        </Typography>
        <List dense sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {cases.length === 0 ? (
            <SectionEmptyState
              title="No cases yet"
              description="Create a case to track behavior goals, chat with the coach, and log incidents."
              actionLabel="New case"
              onAction={() => setNewCaseOpen(true)}
            />
          ) : (
            cases.map((c) => (
              <ListItemButton
                key={c._id}
                selected={selectedCase?._id === c._id}
                onClick={() => {
                  setSelectedCase(c);
                  loadCaseDetails(c._id);
                }}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  py: 0.5,
                }}
              >
                {editingTitle === c._id ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
                    <TextField
                      size="small"
                      value={editTitleValue}
                      onChange={(e) => setEditTitleValue(e.target.value)}
                      placeholder="Case title..."
                      autoFocus
                      fullWidth
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateTitle(c._id);
                        if (e.key === 'Escape') cancelEditingTitle();
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateTitle(c._id);
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        cancelEditingTitle();
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                            {getCaseDisplayTitle(c)}
                          </Typography>
                          <Chip
                            label={`T${c.tier}`}
                            size="small"
                            sx={{ height: 16, fontSize: '0.65rem' }}
                            color={c.status === 'active' ? 'primary' : 'default'}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {c.status} • {new Date(c.createdAt).toLocaleDateString()}
                        </Typography>
                      }
                    />
                    <Tooltip title="Edit title">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditingTitle(c);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="More options">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, c._id)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </ListItemButton>
            ))
          )}
        </List>

        {/* Three-dot Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem onClick={handleDeleteCase}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {selectedCase && (
          <>
            <Divider sx={{ my: 1 }} />

            {/* Selected Case Info */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Current Case: {selectedCase.title || 'Untitled Case'}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => loadCaseDetails(selectedCase._id)}
              sx={{ mt: 1 }}
            >
              Refresh
            </Button>
          </>
        )}
      </Paper>

      {/* New Case Dialog */}
      <Dialog
        open={newCaseOpen}
        onClose={() => setNewCaseOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Case</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Case Title (optional)"
              value={newCaseTitle}
              onChange={(e) => setNewCaseTitle(e.target.value)}
              placeholder={`e.g., "Frequent disruptions in math class"`}
              fullWidth
              helperText="Give this case a descriptive title to easily identify it later"
            />
            <TextField
              select
              label="Tier"
              value={newCaseTier}
              onChange={(e) => setNewCaseTier(Number(e.target.value))}
              fullWidth
            >
              <MenuItem value={1}>Tier 1 - Universal</MenuItem>
              <MenuItem value={2}>Tier 2 - Targeted</MenuItem>
              <MenuItem value={3}>Tier 3 - Intensive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewCaseOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateCase}
            disabled={!newCaseTitle.trim()}
          >
            Create Case
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={!!toast?.open}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
      >
        <Alert
          severity={toast?.sev || 'success'}
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast?.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CasePanel;
